import type { RegistroAuditoria } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PgTable, PgTableColumn, PgChart, PgKpiCard } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { AuditoriaApi } from './auditoria.api.js';
import type { PermissoesAuditoria } from './auditoria.permissoes.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'quando', label: 'Quando' },
  { key: 'entidade', label: 'Entidade' },
  { key: 'acao', label: 'Ação' },
  { key: 'ator', label: 'Ator' },
];

/** Heurística puramente visual sobre o texto real de `acao` (evento do barramento, ex: 'criado'/'excluido'/'falha_login') — nunca um campo novo inventado, só uma cor por padrão de palavra já existente no dado. */
function tomDaAcao(acao: string): 'sucesso' | 'erro' | 'destaque' | 'neutro' {
  const a = acao.toLowerCase();
  if (a.includes('exclu') || a.includes('cancel') || a.includes('falha') || a.includes('recus') || a.includes('estorn')) return 'erro';
  if (a.includes('cri') || a.includes('aprov') || a.includes('pago') || a.includes('confirm') || a.includes('entreg') || a.includes('autentic')) return 'sucesso';
  if (a.includes('atualiz') || a.includes('alter') || a.includes('mud')) return 'destaque';
  return 'neutro';
}

function corDaAcao(tom: 'sucesso' | 'erro' | 'destaque' | 'neutro'): string {
  switch (tom) {
    case 'sucesso':
      return 'var(--pg-color-sucesso)';
    case 'erro':
      return 'var(--pg-color-erro)';
    case 'destaque':
      return 'var(--pg-color-destaque)';
    default:
      return 'var(--pg-color-texto-secundario)';
  }
}

/**
 * `<erp-auditoria-page>` — feed de auditoria real (`GET /api/v1/auditoria`,
 * Core desde a Sprint 0, rota HTTP desde a Sprint 14/Dashboard). Mesmo
 * dado que já alimenta "Atividades recentes" do Dashboard
 * (`DashboardApi.obterAtividadesRecentes`, limite 10) — aqui sem cap
 * artificial de página (limite 100, o maior já usado em qualquer lugar
 * do frontend), com detalhe de antes/depois quando disponível.
 */
export class ErpAuditoriaPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _auditoriaApi: AuditoriaApi | null = null;
  private _permissoes: PermissoesAuditoria = { ler: false };
  private resultado: Resultado<RegistroAuditoria[]> = { status: 'carregando' };
  private registrosAtuais: RegistroAuditoria[] = [];
  private filtroAcao = '';
  private filtroAtor = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set auditoriaApi(valor: AuditoriaApi) {
    this._auditoriaApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesAuditoria) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._auditoriaApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0 0 var(--pg-space-1, 4px); }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-bottom: var(--pg-space-5, 24px); }
        .detalhe { margin-top: var(--pg-space-4, 16px); }
        .detalhe pre { background: var(--pg-color-bg-base, #121417); border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); border-radius: var(--pg-radius-md, 10px); padding: var(--pg-space-3, 12px); font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); overflow-x: auto; max-height: 240px; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 280px); gap: var(--pg-space-4, 16px); align-items: start; }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
        .filtros { display: flex; gap: var(--pg-space-3, 12px); margin-bottom: var(--pg-space-4, 16px); flex-wrap: wrap; }
        ${FORM_FIELD_CSS}
      </style>
      <h1>Logs e Auditoria</h1>
      <div class="subtitulo">Últimos 100 eventos registrados pelo Core — clique numa linha para ver o detalhe</div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Eventos (últimos 100)" icone="auditoria" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-sistema" label="Gerados pelo sistema" icone="auditoria" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-usuarios" label="Gerados por usuários" icone="auditoria" estado="carregando"></pg-kpi-card>
      </div>

      <div class="filtros">
        <select id="filtro-acao"><option value="">Todas as ações</option></select>
        <select id="filtro-ator"><option value="">Todos os atores</option></select>
      </div>

      <div class="layout">
        <pg-card padding="compacto">
          <div id="registros"></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Distribuição por ação</span>
          <div id="donut-acao"><pg-empty-state variant="carregando" titulo="Distribuição por ação"></pg-empty-state></div>
        </pg-card>
      </div>
      <div class="detalhe" id="detalhe"></div>
    `;

    this.root.querySelector('#registros')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ index: number }>).detail;
      const registro = this.registrosFiltrados()[index];
      this.mostrarDetalhe(registro ?? null);
    });

    this.root.querySelector('#filtro-acao')?.addEventListener('change', (evento) => {
      this.filtroAcao = (evento.target as HTMLSelectElement).value;
      this.atualizarTabela();
    });
    this.root.querySelector('#filtro-ator')?.addEventListener('change', (evento) => {
      this.filtroAtor = (evento.target as HTMLSelectElement).value;
      this.atualizarTabela();
    });

    this.atualizarTabela();
  }

  /** Filtro é sempre client-side, sobre os mesmos 100 registros já buscados — não existe endpoint de filtro por ator/ação no backend (`AuditoriaApi.listarRecentes` só aceita `limit`). */
  private registrosFiltrados(): RegistroAuditoria[] {
    return this.registrosAtuais.filter(
      (r) => (!this.filtroAcao || r.acao === this.filtroAcao) && (!this.filtroAtor || r.ator === this.filtroAtor)
    );
  }

  private async carregar(): Promise<void> {
    const api = this._auditoriaApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.atualizarTabela();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizarTabela();
    try {
      this.resultado = await api.listarRecentes(100);
    } catch (erro) {
      logger.erro('falha ao carregar auditoria', { erro });
      this.resultado = { status: 'erro', mensagem: 'falha ao carregar' };
    }
    this.atualizarTabela();
  }

  private atualizarTabela(): void {
    const container = this.root.querySelector('#registros');
    if (!container) return;

    if (this.resultado.status === 'ok') {
      this.registrosAtuais = this.resultado.dado;
      this.popularFiltros(this.registrosAtuais);

      const filtrados = this.registrosFiltrados();
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = filtrados.map((r) => ({
        quando: formatarDataHora(r.criadoEm),
        entidade: `${r.entidade} (${r.acao})`,
        acao: { texto: r.acao, tom: tomDaAcao(r.acao) },
        ator: r.ator === 'system' ? 'sistema' : r.ator,
      }));
      this.atualizarResumo(this.registrosAtuais);
      return;
    }

    this.registrosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultado, 'Logs e Auditoria', 'nenhum evento registrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-sistema', 'kpi-usuarios']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultado.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
    const donut = this.root.querySelector('#donut-acao');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Distribuição por ação" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  /** Preenche as opções dos filtros com os valores REAIS de ação/ator presentes nos 100 registros já buscados — nunca uma lista fixa inventada. Preserva a seleção atual se ainda existir entre as opções. */
  private popularFiltros(registros: RegistroAuditoria[]): void {
    const selectAcao = this.root.querySelector<HTMLSelectElement>('#filtro-acao');
    const selectAtor = this.root.querySelector<HTMLSelectElement>('#filtro-ator');
    if (selectAcao) {
      const acoes = [...new Set(registros.map((r) => r.acao))].sort();
      selectAcao.innerHTML = `<option value="">Todas as ações</option>${acoes.map((a) => `<option value="${escaparHtml(a)}" ${a === this.filtroAcao ? 'selected' : ''}>${escaparHtml(a)}</option>`).join('')}`;
    }
    if (selectAtor) {
      const atores = [...new Set(registros.map((r) => r.ator))].sort();
      selectAtor.innerHTML = `<option value="">Todos os atores</option>${atores.map((a) => `<option value="${escaparHtml(a)}" ${a === this.filtroAtor ? 'selected' : ''}>${escaparHtml(a === 'system' ? 'sistema' : a)}</option>`).join('')}`;
    }
  }

  private atualizarResumo(registros: RegistroAuditoria[]): void {
    const sistema = registros.filter((r) => r.ator === 'system').length;
    const usuarios = registros.length - sistema;

    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(registros.length));
    (this.root.querySelector('#kpi-sistema') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-sistema') as PgKpiCard | null)?.setAttribute('valor', String(sistema));
    (this.root.querySelector('#kpi-usuarios') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-usuarios') as PgKpiCard | null)?.setAttribute('valor', String(usuarios));

    const porAcao = new Map<string, number>();
    for (const r of registros) porAcao.set(r.acao, (porAcao.get(r.acao) ?? 0) + 1);
    const TOP_N = 6;
    const entradas = [...porAcao.entries()].sort((a, b) => b[1] - a[1]);
    const principais = entradas.slice(0, TOP_N);
    const outros = entradas.slice(TOP_N).reduce((soma, [, qtd]) => soma + qtd, 0);

    const donut = this.root.querySelector('#donut-acao');
    if (!donut) return;
    donut.innerHTML = '<pg-chart></pg-chart>';
    const chart = donut.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.points = [
      ...principais.map(([acao, qtd]) => ({ label: acao, values: { valor: qtd }, color: corDaAcao(tomDaAcao(acao)) })),
      ...(outros > 0 ? [{ label: 'Outras', values: { valor: outros }, color: 'var(--pg-color-texto-desabilitado)' }] : []),
    ];
    chart.formatarValor = (valor) => String(valor);
  }

  private mostrarDetalhe(registro: RegistroAuditoria | null): void {
    const container = this.root.querySelector('#detalhe');
    if (!container) return;
    if (!registro) {
      container.innerHTML = '';
      return;
    }
    container.innerHTML = `
      <pg-card>
        <span slot="header">Detalhe — ${escaparHtml(registro.entidade)} · ${escaparHtml(registro.acao)}</span>
        <p style="font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin: 0 0 var(--pg-space-3, 12px);">
          Entidade ID: ${escaparHtml(registro.entidadeId)} · Ator: ${escaparHtml(registro.ator === 'system' ? 'sistema' : registro.ator)} · ${escaparHtml(formatarDataHora(registro.criadoEm))}
        </p>
        <pre>${escaparHtml(JSON.stringify({ antes: registro.antes, depois: registro.depois }, null, 2))}</pre>
      </pg-card>
    `;
  }
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum registro', descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-auditoria-page', ErpAuditoriaPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-auditoria-page': ErpAuditoriaPage;
  }
}
